import React, { useMemo, type MouseEvent } from 'react';
import { styled } from '@compiled/react';
import EmptyState from '@atlaskit/empty-state';
import Pagination from '@atlaskit/pagination';
import { token } from '@atlaskit/tokens';

import { SERVICE_DESK_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';

import { fg } from '@atlassian/jira-feature-gating';
import ErrorIllustration from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/error/components/error/index.tsx';
import { getAkEmptyStateRenderImageFn } from '@atlassian/jira-illustrations/src/ui/helpers/ak-empty-state/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useObjectConnectedIssuesPaginated } from '@atlassian/jira-servicedesk-insight-object-services/src/services/object-connected-issues-paginated/index.tsx';
import type { CmdbObjectId } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import { IssueLineCardGroup } from '@atlassian/jira-servicedesk-issue-line-card-group/src/main.tsx';
import { LinkedIssuesSkeleton } from '../../../common/ui/skeletons/index.tsx';
import messages from './messages.tsx';

export type Props = {
	objectId: CmdbObjectId;
};

// @ts-expect-error - TS7006 - Parameter 'issue' implicitly has an 'any' type.
const formatIssue = (issue) => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const newIssue: Record<string, any> = {};
	newIssue.id = issue.id.toString();
	newIssue.isLoading = false;
	newIssue.issueSummary = issue.title;
	newIssue.issueKey = issue.key;
	newIssue.issueLink = `/browse/${issue.key || ''}`;
	newIssue.issueTypeName = issue.type.name;
	newIssue.issueTypeIconUrl = issue.type.iconUrl;
	newIssue.issuePriorityName = issue.priority ? issue.priority.name : '';
	newIssue.issuePriorityUrl = issue.priority ? issue.priority.iconUrl : '';
	newIssue.projectType = SERVICE_DESK_PROJECT;
	return newIssue;
};

const ErrorState = () => {
	const { formatMessage } = useIntl();

	const renderImage = () =>
		getAkEmptyStateRenderImageFn(ErrorIllustration)({
			imageWidth: 120,
			maxImageWidth: 120,
			maxImageHeight: 212,
		});

	return (
		<EmptyState
			width="wide"
			renderImage={renderImage}
			header={formatMessage(
				fg('itsm-issue-renaming')
					? messages.errorStateHeaderIssueTermRefresh
					: messages.errorStateHeader,
			)}
			description={formatMessage(messages.errorStateDescription)}
		/>
	);
};

export const LinkedIssues = ({ objectId }: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	const [{ linkedIssues, loading, error, pages, pageIndex }, { onPageChange }] =
		useObjectConnectedIssuesPaginated(objectId);

	const linkedIssuesFormatted = useMemo(
		() => linkedIssues && linkedIssues.map((issue) => formatIssue(issue)),
		[linkedIssues],
	);

	if (loading) {
		return <LinkedIssuesSkeleton />;
	}

	if (error) {
		return <ErrorState />;
	}

	if (!linkedIssues || linkedIssues.length === 0) {
		return (
			// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
			<p data-testid="servicedesk-insight-object-graph.ui.object-view.linked-issues.empty-state">
				{formatMessage(
					fg('itsm-issue-renaming')
						? messages.noLinkedIssuesIssueTermRefresh
						: messages.noLinkedIssues,
				)}
			</p>
		);
	}

	const handleClick = (event: MouseEvent<HTMLElement>, page: number) => {
		onPageChange(page);
	};

	return linkedIssuesFormatted && linkedIssuesFormatted.length > 0 ? (
		<>
			<LinkedIssuesContainer>
				{/* @ts-expect-error - TS2769 - No overload matches this call. */}
				<IssueLineCardGroup issues={linkedIssuesFormatted} groupId="linked-issues-group" />
			</LinkedIssuesContainer>
			{pages !== undefined && (
				<PaginationWrapper>
					{/* @ts-expect-error - TS2322 - Type '(event: MouseEvent<HTMLElement>, page: number) => void' is not assignable to type '(event: SyntheticEvent<Element, Event>, page: number, analyticsEvent?: UIAnalyticsEvent | undefined) => void'. */}
					<Pagination selectedIndex={pageIndex} pages={pages} onChange={handleClick} />
				</PaginationWrapper>
			)}
		</>
	) : null;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LinkedIssuesContainer = styled.div({
	overflow: 'auto',
	paddingBottom: token('space.100'),
	maxHeight: '480px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PaginationWrapper = styled.div({
	display: 'flex',
	justifyContent: 'center',
});
